const SAMPLES = 24

// Reconstructs N(x), V(x), M(x) along an element from its nodal end forces plus the applied
// element loads. Sign conventions were calibrated against known analytical solutions (simply
// supported/fixed-fixed beams under UDL, cantilever under tip load, midspan point load and
// point moment) because the backend's nodal end-force sign convention does not match the usual
// diagram sign convention (see HANDOFF.md). Validated relations:
//   V(x) = Vi + integral(qy) + sum(Py jumps)              , V(L) should equal -Vj
//   M(x) = -Mi + integral(V) - sum(Mz jumps)              , M(L) should equal Mj
//   N(x) = -Ni + integral(qx) + sum(Px jumps)              , N(L) should equal Nj
function cumulativeTrapezoid(values, xs) {
  const out = [0]
  for (let i = 1; i < xs.length; i += 1) {
    const dx = xs[i] - xs[i - 1]
    out.push(out[i - 1] + ((values[i] + values[i - 1]) / 2) * dx)
  }
  return out
}

function sumPointJumps(pointLoads, x, field) {
  return pointLoads
    .filter((load) => (Number(load.position) || 0) <= x + 1e-9)
    .reduce((sum, load) => sum + (Number(load[field]) || 0), 0)
}

// A point load creates a genuine step discontinuity in N/V (and Mz a step in M). If the only
// samples straddling that position are a full grid cell apart, trapezoidal integration of V into
// M averages the pre- and post-jump values across that whole cell instead of treating the jump as
// instantaneous, biasing the integral by roughly (jump * cellWidth / 2) for every subsequent
// sample — which is exactly the wrong nonzero end moment this was fixed for. Inserting a pair of
// samples a hair's width before/after each point load's position keeps the step effectively
// instantaneous regardless of how coarse the regular grid is.
function buildSamplePositions(length, pointLoads) {
  const base = Array.from({ length: SAMPLES + 1 }, (_, i) => (i / SAMPLES) * length)
  const epsilon = Math.max(length * 1e-5, 1e-7)
  const extra = []
  pointLoads.forEach((load) => {
    const position = Math.min(Math.max(Number(load.position) || 0, 0), length)
    extra.push(Math.max(position - epsilon, 0))
    extra.push(Math.min(position + epsilon, length))
  })
  const merged = [...base, ...extra].sort((a, b) => a - b)
  return merged.filter((x, i) => i === 0 || x - merged[i - 1] > 1e-9)
}

// The N/V/M reconstruction below works entirely in the element's local frame (axial x, transverse
// y), so a load entered in global coordinates must first be projected onto the local axes — every
// component along axial, every component along transverse. The local axes match the backend
// transform (matrixSolver.js): e_x = (c, s) points I->J and e_y = (-s, c). A moment component (Mz)
// is orientation-invariant in 2D. Loads entered in local coordinates already live in this frame and
// pass through unchanged. Skipping this projection is only harmless when e_x aligns with the global
// x axis (a horizontal member, c=1/s=0); on an inclined member it wrongly feeds the global
// components straight into the local integration, producing nonzero end moments and a bogus jump at
// the far node — the exact symptom this projection fixes.
function toLocalAxial(gx, gy, axis) {
  return gx * axis.c + gy * axis.s
}

function toLocalTransverse(gx, gy, axis) {
  return -gx * axis.s + gy * axis.c
}

function resolveDistributedLocal(load, axis) {
  const gxI = Number(load.qxI) || 0
  const gxJ = Number(load.qxJ ?? load.qxI) || 0
  const gyI = Number(load.qyI) || 0
  const gyJ = Number(load.qyJ ?? load.qyI) || 0
  if (load.coordinateSystem !== 'global') return { qxI: gxI, qxJ: gxJ, qyI: gyI, qyJ: gyJ }
  return {
    qxI: toLocalAxial(gxI, gyI, axis),
    qxJ: toLocalAxial(gxJ, gyJ, axis),
    qyI: toLocalTransverse(gxI, gyI, axis),
    qyJ: toLocalTransverse(gxJ, gyJ, axis),
  }
}

function resolvePointLocal(load, axis) {
  const gx = Number(load.Px) || 0
  const gy = Number(load.Py) || 0
  const Mz = Number(load.Mz) || 0
  if (load.coordinateSystem !== 'global') return { position: load.position, Px: gx, Py: gy, Mz }
  return { position: load.position, Px: toLocalAxial(gx, gy, axis), Py: toLocalTransverse(gx, gy, axis), Mz }
}

export function computeElementDiagram(elementResult, elementLoadsForElement, axis = { c: 1, s: 0 }) {
  const length = elementResult.length
  const { Ni, Vi, Mi } = elementResult.localEndForces
  // Combination views can carry more than one distributed load per element (one per contributing
  // load case, already scaled by its factor) — they're linear, so their qxI/qxJ/qyI/qyJ just add up.
  // Each load is first projected onto the element's local axes so global-coordinate loads on inclined
  // members contribute their true axial/transverse split rather than their raw global components.
  const distributedLoads = elementLoadsForElement
    .filter((l) => l.type === 'distributed')
    .map((l) => resolveDistributedLocal(l, axis))
  const pointLoads = elementLoadsForElement.filter((l) => l.type === 'point').map((l) => resolvePointLocal(l, axis))

  const qxI = distributedLoads.reduce((sum, l) => sum + l.qxI, 0)
  const qxJ = distributedLoads.reduce((sum, l) => sum + l.qxJ, 0)
  const qyI = distributedLoads.reduce((sum, l) => sum + l.qyI, 0)
  const qyJ = distributedLoads.reduce((sum, l) => sum + l.qyJ, 0)

  const xs = buildSamplePositions(length, pointLoads)
  const qxValues = xs.map((x) => qxI + ((qxJ - qxI) * x) / length)
  const qyValues = xs.map((x) => qyI + ((qyJ - qyI) * x) / length)
  const intQx = cumulativeTrapezoid(qxValues, xs)
  const intQy = cumulativeTrapezoid(qyValues, xs)

  const Vs = xs.map((x, i) => Vi + intQy[i] + sumPointJumps(pointLoads, x, 'Py'))
  // N mirrors V's sign pattern (+integral, +jumps) rather than subtracting: the original
  // "-Ni - integral(qx)" form was never exercised by a case with nonzero qx and produced double
  // the correct end value on axially-loaded members (e.g. self-weight on a column) instead of
  // landing on Nj at x = length.
  const Ns = xs.map((x, i) => -Ni + intQx[i] + sumPointJumps(pointLoads, x, 'Px'))
  const intV = cumulativeTrapezoid(Vs, xs)
  const Ms = xs.map((x, i) => -Mi + intV[i] - sumPointJumps(pointLoads, x, 'Mz'))

  return { xs, N: Ns, V: Vs, M: Ms, length }
}

// Mirrors the backend's automatic self-weight load (see HANDOFF.md): a case marked `selfWeight`
// gets an implicit uniform distributed load per element, q = specificWeight * A, acting downward
// in the global frame. The frontend never puts this load in `elementLoads` (the backend generates
// it server-side), so the diagram reconstruction has to synthesize the same load here or the N/V/M
// curves silently ignore it — showing e.g. a constant shear instead of the correct linear one.
function buildSelfWeightLoads(data, scaledFactor) {
  return (data.elements || []).flatMap((el) => {
    const specificWeight = Number(el.material?.specificWeight)
    if (!Number.isFinite(specificWeight)) return []
    const A = Number(el.A) || 0
    const q = -(specificWeight * A) * scaledFactor
    return [{ element: el.id, type: 'distributed', coordinateSystem: 'global', qxI: 0, qxJ: 0, qyI: q, qyJ: q }]
  })
}

// Resolves the effective element loads for whichever load case or combination is being viewed, so
// the N/V/M reconstruction above matches whatever the backend actually solved for that view. For a
// single case this is just that case's own elementLoads; for a combination it's every contributing
// case's elementLoads scaled by that case's factor (matching the backend's own combination math).
export function resolveElementLoadsForView(data, view) {
  if (!data || !view) return []

  if (view.type === 'case') {
    const loadCase = (data.loadCases || []).find((c) => c.id === view.id)
    if (!loadCase) return []
    const loads = loadCase.elementLoads || []
    return loadCase.selfWeight ? [...loads, ...buildSelfWeightLoads(data, 1)] : loads
  }

  const combination = (data.combinations || []).find((c) => c.id === view.id)
  if (!combination) return []

  const factors = combination.factors || {}
  const combined = []
  Object.entries(factors).forEach(([caseId, factor]) => {
    const loadCase = (data.loadCases || []).find((c) => c.id === caseId)
    if (!loadCase) return
    const scaledFactor = Number(factor) || 0
    ;(loadCase.elementLoads || []).forEach((load) => {
      if (load.type === 'point') {
        combined.push({
          ...load,
          Px: (Number(load.Px) || 0) * scaledFactor,
          Py: (Number(load.Py) || 0) * scaledFactor,
          Mz: (Number(load.Mz) || 0) * scaledFactor,
        })
      } else {
        combined.push({
          ...load,
          qxI: (Number(load.qxI) || 0) * scaledFactor,
          qxJ: (Number(load.qxJ ?? load.qxI) || 0) * scaledFactor,
          qyI: (Number(load.qyI) || 0) * scaledFactor,
          qyJ: (Number(load.qyJ ?? load.qyI) || 0) * scaledFactor,
        })
      }
    })
    if (loadCase.selfWeight) combined.push(...buildSelfWeightLoads(data, scaledFactor))
  })
  return combined
}

const DEFORMED_SAMPLES = 20

function interpolateAtLength(values, xs, target) {
  if (target <= xs[0]) return values[0]
  const last = xs.length - 1
  if (target >= xs[last]) return values[last]
  for (let i = 1; i < xs.length; i += 1) {
    if (target <= xs[i]) {
      const span = xs[i] - xs[i - 1] || 1
      const t = (target - xs[i - 1]) / span
      return values[i - 1] + (values[i] - values[i - 1]) * t
    }
  }
  return values[last]
}

// The transverse deflection relative to the chord joining the two (displaced) end nodes, obtained
// by the moment-area method: curvature is M(x)/EI, so double-integrating M(x) and removing the
// linear part (so the result vanishes at both ends) reproduces the exact bow shape — including the
// interior deflection from member loads that the cubic-Hermite-of-nodal-DOFs reconstruction cannot
// see when both ends are restrained (a fixed-fixed member bows purely from its load, with every
// nodal DOF ~0). EI is unknown here, but the shape is EI-independent up to one scalar; we pin that
// scalar and its sign with the backend-reported peak deflection so the curve passes exactly through
// it. Returns a bow value per uniform sample i/samples, or null when there's nothing to reconstruct.
function buildMomentAreaBow(moment, transverseExt, L, samples) {
  if (!moment || !moment.xs || moment.xs.length < 2) return null
  const peak = Number(transverseExt?.value)
  if (!Number.isFinite(peak) || peak === 0) return null

  const { xs, M } = moment
  const slope = cumulativeTrapezoid(M, xs)
  const doubleInt = cumulativeTrapezoid(slope, xs)
  const endValue = doubleInt[doubleInt.length - 1]
  // Proportional to the true bow (unknown EI factor and sign), zeroed at both ends.
  const rel = doubleInt.map((value, i) => value - (endValue * xs[i]) / L)

  const xiPeak = Math.min(Math.max(Number(transverseExt?.xi) || 0, 0), 1)
  const relAtPeak = interpolateAtLength(rel, xs, xiPeak * L)
  if (Math.abs(relAtPeak) < 1e-15) return null
  const k = peak / relAtPeak

  const bow = []
  for (let i = 0; i <= samples; i += 1) {
    bow.push(k * interpolateAtLength(rel, xs, (i / samples) * L))
  }
  return bow
}

// Reconstructs an element's deflected shape (in model coordinates) from its local end
// displacements: linear interpolation of the axial component and cubic Hermite of the transverse
// component (theta as the local slope dv/ds). Local axes match the backend transform
// (matrixSolver.js): e_x = (c, s) points I->J and e_y = (-s, c). Using localDisplacements (rather
// than the global nodal ones) keeps the curve correct at moment releases, where the member's end
// rotation differs from the shared nodal rotation. When `options.moment` (the element's M(x)
// diagram) and `options.transverseExt` (the reported peak deflection) are supplied, the transverse
// component is instead reconstructed from the moment diagram so member-load bows show even when the
// nodal DOFs are all ~0 (fixed-fixed members). `scale` amplifies the tiny displacements so the
// deformed shape is visible.
export function computeDeformedElementPoints(
  nodeI,
  nodeJ,
  localDisplacements,
  scale,
  options = {},
  samples = DEFORMED_SAMPLES
) {
  const dx = nodeJ.x - nodeI.x
  const dy = nodeJ.y - nodeI.y
  const L = Math.hypot(dx, dy) || 1
  const ex = { x: dx / L, y: dy / L }
  const ey = { x: -ex.y, y: ex.x }
  const { ui, vi, thetaI, uj, vj, thetaJ } = localDisplacements
  const bow = buildMomentAreaBow(options.moment, options.transverseExt, L, samples)

  const points = []
  for (let i = 0; i <= samples; i += 1) {
    const xi = i / samples
    const uL = ui + (uj - ui) * xi
    let vL
    if (bow) {
      // Chord between the displaced end nodes plus the moment-area bow relative to that chord.
      vL = vi + (vj - vi) * xi + bow[i]
    } else {
      const h1 = 1 - 3 * xi ** 2 + 2 * xi ** 3
      const h2 = L * (xi - 2 * xi ** 2 + xi ** 3)
      const h3 = 3 * xi ** 2 - 2 * xi ** 3
      const h4 = L * (-(xi ** 2) + xi ** 3)
      vL = h1 * vi + h2 * thetaI + h3 * vj + h4 * thetaJ
    }

    const baseX = nodeI.x + ex.x * xi * L
    const baseY = nodeI.y + ex.y * xi * L
    const dispX = ex.x * uL + ey.x * vL
    const dispY = ex.y * uL + ey.y * vL
    points.push({ x: baseX + scale * dispX, y: baseY + scale * dispY })
  }
  return points
}

export function computeAllDiagrams(elements, resultElements, elementLoads, nodesById = {}) {
  const diagramsByElementId = {}
  const maxAbs = { N: 0, V: 0, M: 0 }

  elements.forEach((el) => {
    const elementResult = resultElements.find((r) => r.id === el.id)
    if (!elementResult) return
    const loadsForElement = elementLoads.filter((l) => l.element === el.id)
    // Local axis e_x = (c, s) points nodeI->nodeJ; used to project global-coordinate loads into the
    // element frame. Falls back to the global x axis when node coordinates are unavailable.
    const nodeI = nodesById[el.nodeI]
    const nodeJ = nodesById[el.nodeJ]
    let axis = { c: 1, s: 0 }
    if (nodeI && nodeJ) {
      const dx = Number(nodeJ.x) - Number(nodeI.x)
      const dy = Number(nodeJ.y) - Number(nodeI.y)
      const L = Math.hypot(dx, dy) || 1
      axis = { c: dx / L, s: dy / L }
    }
    const diagram = computeElementDiagram(elementResult, loadsForElement, axis)
    diagramsByElementId[el.id] = diagram
    ;['N', 'V', 'M'].forEach((type) => {
      const localMax = Math.max(...diagram[type].map((v) => Math.abs(v)))
      if (localMax > maxAbs[type]) maxAbs[type] = localMax
    })
  })

  return { diagramsByElementId, maxAbs }
}
